import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import {
  acquireConsent,
  archiveExpensePlan,
  archiveSavingsPlan,
  createCustomTransactionCategory,
  createExpensePlan,
  createSavingsPlan,
  editCustomTransactionCategory,
  editExpensePlan,
  editTransactionCategory,
  incrementSavingsPlanAchievedAmount,
  initiateConsent,
  markNewAccountsAsSeen,
  modifyAccounts,
  modifySavingsPlan,
  removeAccount,
  removeBank,
  removeCustomTransactionCategory,
  removeExpensePlan,
  removeSavingsPlan,
  setAccountAsMain,
  toggleCustomerBudgetPreferences,
  unarchiveExpensePlan,
  unarchiveSavingsPlan,
} from "./services";

export const useInitiateConsentMutation = () =>
  useMutation({
    mutationFn: initiateConsent,
  });

export const useAcquireConsentMutation = () =>
  useMutation({
    mutationFn: acquireConsent,
  });

export const useSetAccountAsMainMutation = () =>
  useMutation({
    mutationFn: setAccountAsMain,
    onSuccess: async () =>
      await queryClient.invalidateQueries({ queryKey: ["budget"] }),
  });

export const useModifyAccountsMutation = () =>
  useMutation({
    mutationFn: modifyAccounts,
    onSuccess: async () =>
      await queryClient.invalidateQueries({ queryKey: ["budget"] }),
  });

export const useRemoveAccountMutation = () =>
  useMutation({
    mutationFn: removeAccount,
    onSuccess: async () =>
      await queryClient.invalidateQueries({ queryKey: ["budget"] }),
  });

export const useRemoveBankMutation = () =>
  useMutation({
    mutationFn: removeBank,
    onSuccess: async () =>
      await queryClient.invalidateQueries({ queryKey: ["budget"] }),
  });

export const useMarkNewAccountsAsSeenMutation = () =>
  useMutation({
    mutationFn: markNewAccountsAsSeen,
    onSuccess: async () =>
      await queryClient.invalidateQueries({
        queryKey: ["budget"],
      }),
  });

export const useCreateSavingsPlanMutation = () =>
  useMutation({
    mutationFn: createSavingsPlan,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "savingsPlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "settings"],
        }),
      ]);
    },
  });

export const useModifySavingsPlanMutation = () =>
  useMutation({
    mutationFn: modifySavingsPlan,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "savingsPlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "settings"],
        }),
      ]);
    },
  });

export const useIncrementSavingsPlanAchievedAmountMutation = () =>
  useMutation({
    mutationFn: incrementSavingsPlanAchievedAmount,
    onSuccess: async () =>
      await queryClient.invalidateQueries({
        queryKey: ["budget", "savingsPlans"],
      }),
  });

export const useRemoveSavingsPlanMutation = () =>
  useMutation({
    mutationFn: removeSavingsPlan,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "savingsPlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "settings"],
        }),
      ]);
    },
  });

export const useArchiveSavingsPlanMutation = () =>
  useMutation({
    mutationFn: archiveSavingsPlan,
    onSuccess: async () =>
      await queryClient.invalidateQueries({
        queryKey: ["budget", "savingsPlans"],
      }),
  });

export const useUnarchiveSavingsPlanMutation = () =>
  useMutation({
    mutationFn: unarchiveSavingsPlan,
    onSuccess: async () =>
      await queryClient.invalidateQueries({
        queryKey: ["budget", "savingsPlans"],
      }),
  });

export const useArchiveExpensePlanMutation = () =>
  useMutation({
    mutationFn: archiveExpensePlan,
    onSuccess: async () =>
      await queryClient.invalidateQueries({
        queryKey: ["budget", "expensePlans"],
      }),
  });

export const useUnarchiveExpensePlanMutation = () =>
  useMutation({
    mutationFn: unarchiveExpensePlan,
    onSuccess: async () =>
      await queryClient.invalidateQueries({
        queryKey: ["budget", "expensePlans"],
      }),
  });

export const useCreateExpensePlanMutation = () =>
  useMutation({
    mutationFn: createExpensePlan,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "customTransactionCategory"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "settings"],
        }),
      ]);
    },
  });

export const useEditExpensePlanMutation = () =>
  useMutation({
    mutationFn: editExpensePlan,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlan"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "settings"],
        }),
      ]);
    },
  });

export const useRemoveExpensePlanMutation = () =>
  useMutation({
    mutationFn: removeExpensePlan,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "customTransactionCategory"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "settings"],
        }),
      ]);
    },
  });

export const useEditTransactionCategoryMutation = () =>
  useMutation({
    mutationFn: editTransactionCategory,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "transactions"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlan"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "financeBreakdown"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "weeklyExpenseStatistics"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "customTransactionCategory"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "transaction"],
        }),
      ]);
    },
  });

export const useCreateCustomTransactionCategoryMutation = () =>
  useMutation({
    mutationFn: createCustomTransactionCategory,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "metadata"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "transactions"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlan"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "financeBreakdown"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "weeklyExpenseStatistics"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "customTransactionCategory"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "transaction"],
        }),
      ]);
    },
  });

export const useEditCustomTransactionCategoryMutation = () =>
  useMutation({
    mutationFn: editCustomTransactionCategory,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "metadata"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "transactions"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlan"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "financeBreakdown"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "weeklyExpenseStatistics"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "customTransactionCategory"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "transaction"],
        }),
      ]);
    },
  });

export const useRemoveCustomTransactionCategoryMutation = () =>
  useMutation({
    mutationFn: removeCustomTransactionCategory,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["budget", "metadata"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "transactions"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlan"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "expensePlans"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "financeBreakdown"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "weeklyExpenseStatistics"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "customTransactionCategory"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["budget", "transaction"],
        }),
      ]);
    },
  });

export const useToggleCustomerBudgetPreferencesMutation = () =>
  useMutation({
    mutationFn: toggleCustomerBudgetPreferences,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["budget"] });
    },
  });
